/* eslint-disable react-hooks/exhaustive-deps */
import { Divider, Grid, Stack, Typography } from "@mui/material";
import { stellaInspectionNoteBusiness } from "api/bussiness/inspection-note/stella-inspection-note/stella-inspection-note-business";

import { AppearanceTypeEnum } from "commons/enum/appearance-type";
import { PrintPaperSPUnitEnum } from "commons/enum/foil-unit";
import { StellaInspectionsPermission } from "commons/enum/permission";
import { numberFormatAsString } from "commons/ultilities";
import { dateTimeDisplay } from "commons/ultilities/date/dayjs";
import { observer } from "mobx-react-lite";
import { isAlive } from "mobx-state-tree";
import { useStores } from "mobx-stores/_root-store";
import { useCallback, useEffect, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { PropertyInfo } from "share-components/atomics/property-info";
import {
  EditButton,
  GoBackIconButton,
  PrintButton,
  RemoveButton,
} from "share-components/mui-wrap/button/action-button";
import { PageContentScrollableContainer } from "share-components/templates/page/page-content-container";
import { PageDetailContainer } from "share-components/templates/page/page-detail-container";
import { PageHeaderContainer } from "share-components/templates/page/page-header-container";

import { EditStellaInspectionNote } from "../edit-inspection-note/edit-inspection-note";
import {
  createQuantification,
  getLabelOfAppearance,
  getLabelOfConclusionStatus,
  getLabelOfPrintPaperSPUnit,
} from "../share";
import { StellaInspectionNotePrintPreview } from "./inspection-print-preview";

export const StellaInspectionNoteDetail = observer(() => {
  const { inspectionNoteId } = useParams();
  const {
    stellaInspectionNoteManagement: {
      getStellaInspectionNoteDetail,
      openEdit,
      inspectionPrintingManagement: { open },
    },
  } = useStores();
  useEffect(() => {
    if (inspectionNoteId) {
      getStellaInspectionNoteDetail(inspectionNoteId);
    }
  }, [getStellaInspectionNoteDetail, inspectionNoteId]);
  return (
    <PageDetailContainer>
      <PageHeaderContainer direction={"row"} justifyContent={"space-between"}>
        <Header></Header>
      </PageHeaderContainer>
      <PageContentScrollableContainer>
        <Content></Content>
      </PageContentScrollableContainer>

      <>{openEdit && <EditStellaInspectionNote></EditStellaInspectionNote>}</>
      <>{open && <StellaInspectionNotePrintPreview />}</>
    </PageDetailContainer>
  );
});
type HeaderProps = {};
const Header = observer((props: HeaderProps) => {
  const {
    stellaInspectionNoteManagement: {
      inspectionNoteDetail,
      setProp: setInspectionNoteManagementProp,
      inspectionPrintingManagement,
    },
    deleteConfirmationStore: { setProp, setDeletionCallback },
  } = useStores();
  const navigate = useNavigate();
  const goBack = useCallback(() => {
    navigate(-1);
  }, [navigate]);
  const onDelete = useCallback(
    async (id: string) => {
      if (id) {
        await stellaInspectionNoteBusiness.delete(id);
        goBack();
      }
    },
    [goBack],
  );
  const removeHandler = useCallback(() => {
    setProp("open", true);
    setDeletionCallback(() => {
      if (!inspectionNoteDetail?.id) return;
      onDelete(inspectionNoteDetail?.id);
    });
  }, [inspectionNoteDetail, onDelete, setDeletionCallback, setProp]);

  const editHandler = useCallback(() => {
    setInspectionNoteManagementProp("openEdit", true);
  }, [setInspectionNoteManagementProp]);

  const openPrintPreviewHandler = useCallback(() => {
    inspectionPrintingManagement.setProp("open", true);
  }, [inspectionPrintingManagement]);

  return (
    <>
      <Stack
        direction={"row"}
        justifyItems={"center"}
        spacing={1}
        alignItems={"center"}
      >
        <GoBackIconButton onClick={goBack}></GoBackIconButton>
        <Typography variant="subtitle2">{`Thông tin chi tiết `}</Typography>
      </Stack>
      <Stack direction={"row"} justifyItems={"center"} spacing={1}>
        <PrintButton
          accessPermission={[StellaInspectionsPermission.Print]}
          onClick={openPrintPreviewHandler}
        >
          Phiếu kiểm nghiệm
        </PrintButton>
        <RemoveButton
          accessPermission={[StellaInspectionsPermission.Delete]}
          onClick={removeHandler}
        ></RemoveButton>
        <EditButton
          accessPermission={[StellaInspectionsPermission.Update]}
          onClick={editHandler}
        ></EditButton>
      </Stack>
    </>
  );
});

const Content = observer(() => {
  const {
    stellaInspectionNoteManagement: { inspectionNoteDetail },
  } = useStores();

  const firstItem = useMemo(() => {
    if (
      inspectionNoteDetail &&
      isAlive(inspectionNoteDetail) &&
      inspectionNoteDetail?.items &&
      inspectionNoteDetail?.items.length > 0 &&
      isAlive(inspectionNoteDetail?.items)
    ) {
      return inspectionNoteDetail?.items?.[0];
    }
    return null;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    inspectionNoteDetail,
    inspectionNoteDetail?.items,
    JSON.stringify(inspectionNoteDetail?.items),
  ]);
  return (
    <Stack>
      <Grid container spacing={2}>
        <Grid item xs={6}>
          <PropertyInfo
            title="Số phiếu:"
            value={inspectionNoteDetail?.number || ""}
          ></PropertyInfo>
        </Grid>

        <Grid item xs={6}>
          <PropertyInfo
            title="Ngày kiểm nghiệm:"
            value={dateTimeDisplay(inspectionNoteDetail?.inspectionDate || "")}
          ></PropertyInfo>
        </Grid>

        <Grid item xs={6}>
          <PropertyInfo
            title="Ngày sản xuất:"
            value={dateTimeDisplay(
              inspectionNoteDetail?.manufacturingDate || "",
            )}
          ></PropertyInfo>
        </Grid>

        <Grid item xs={6}>
          <PropertyInfo
            title="Ngày hết hạn:"
            value={dateTimeDisplay(inspectionNoteDetail?.expireDate || "")}
          ></PropertyInfo>
        </Grid>

        <Grid item xs={6}>
          <PropertyInfo
            title="Lệnh sản xuất:"
            value={
              inspectionNoteDetail?.workOrderPO?.woNumber ||
              inspectionNoteDetail?.workOrder?.woNumber ||
              ""
            }
          ></PropertyInfo>
        </Grid>

        <Grid item xs={6}>
          <PropertyInfo
            title="Khách hàng:"
            value={inspectionNoteDetail?.customer?.dealingName || ""}
          ></PropertyInfo>
        </Grid>

        <Grid item xs={6}>
          <PropertyInfo
            title="Tên sản phẩm:"
            value={firstItem?.product?.name || ""}
          ></PropertyInfo>
        </Grid>

        <Grid item xs={6}>
          <PropertyInfo
            title="Mã sản phẩm:"
            value={firstItem?.product?.code || ""}
          ></PropertyInfo>
        </Grid>

        <Grid item xs={12}>
          <PropertyInfo
            title="Kết luận:"
            value={inspectionNoteDetail?.conclusion || ""}
          ></PropertyInfo>
        </Grid>

        <Grid item xs={12}>
          <Divider></Divider>
        </Grid>
        <Grid item xs={4}>
          <PropertyInfo
            title="Tiêu chuẩn - Ngoại quan:"
            value={
              <Stack>
                <Typography>- Màu sắc chuẩn, rõ nét.</Typography>
                {firstItem?.appearance && (
                  <Typography>{`- ${getLabelOfAppearance(
                    firstItem?.appearance as AppearanceTypeEnum,
                  )}`}</Typography>
                )}
              </Stack>
            }
          ></PropertyInfo>
        </Grid>

        <Grid item xs={4}>
          <PropertyInfo
            title="Kết quả:"
            value={firstItem?.appearanceResult || "-"}
          ></PropertyInfo>
        </Grid>

        <Grid item xs={4}>
          <PropertyInfo
            title="Kết luận:"
            value={
              (firstItem?.appearanceConclusion &&
                getLabelOfConclusionStatus(firstItem?.appearanceConclusion)) ||
              ""
            }
          ></PropertyInfo>
        </Grid>
        <Grid item xs={12}>
          <Divider></Divider>
        </Grid>

        <Grid item xs={4}>
          <PropertyInfo
            title="Tiêu chuẩn - Quy cách dài:"
            value={`${numberFormatAsString(
              firstItem?.product?.dimensionWeight?.lengthStandard?.toString() ||
                "-",
            )} (± 1 mm)`}
          ></PropertyInfo>
        </Grid>

        <Grid item xs={4}>
          <PropertyInfo
            title="Kết quả:"
            value={
              (numberFormatAsString(
                firstItem?.lengthResult?.toString() || "",
              ) || "-") + " mm"
            }
          ></PropertyInfo>
        </Grid>

        <Grid item xs={4}>
          <PropertyInfo
            title="Kết luận:"
            value={
              (firstItem?.lengthConclusion &&
                getLabelOfConclusionStatus(firstItem?.lengthConclusion)) ||
              ""
            }
          ></PropertyInfo>
        </Grid>

        <Grid item xs={12}>
          <Divider></Divider>
        </Grid>

        <Grid item xs={4}>
          <PropertyInfo
            title="Tiêu chuẩn - Quy cách rộng:"
            value={`${numberFormatAsString(
              firstItem?.product?.dimensionWeight?.widthStandard?.toString() ||
                "-",
            )} (± 1 mm)`}
          ></PropertyInfo>
        </Grid>

        <Grid item xs={4}>
          <PropertyInfo
            title="Kết quả:"
            value={
              (numberFormatAsString(firstItem?.widthResult?.toString() || "") ||
                "-") + " mm"
            }
          ></PropertyInfo>
        </Grid>

        <Grid item xs={4}>
          <PropertyInfo
            title="Kết luận:"
            value={
              (firstItem?.widthConclusion &&
                getLabelOfConclusionStatus(firstItem?.widthConclusion)) ||
              ""
            }
          ></PropertyInfo>
        </Grid>
        <Grid item xs={12}>
          <Divider></Divider>
        </Grid>

        <Grid item xs={4}>
          <PropertyInfo
            title="Tiêu chuẩn - Quy cách cao:"
            value={`${numberFormatAsString(
              firstItem?.product?.dimensionWeight?.heightStandard?.toString() ||
                "-",
            )} (± 1 mm)`}
          ></PropertyInfo>
        </Grid>

        <Grid item xs={4}>
          <PropertyInfo
            title="Kết quả:"
            value={
              (numberFormatAsString(
                firstItem?.heightResult?.toString() || "",
              ) || "-") + " mm"
            }
          ></PropertyInfo>
        </Grid>

        <Grid item xs={4}>
          <PropertyInfo
            title="Kết luận:"
            value={
              (firstItem?.heightConclusion &&
                getLabelOfConclusionStatus(firstItem?.heightConclusion)) ||
              ""
            }
          ></PropertyInfo>
        </Grid>
        <Grid item xs={12}>
          <Divider></Divider>
        </Grid>

        <Grid item xs={4}>
          <PropertyInfo
            title="Tiêu chuẩn - Định lượng:"
            value={`${
              firstItem?.product &&
              (createQuantification(firstItem?.product) || "")
            } (5/+10 g/m2)`}
          ></PropertyInfo>
        </Grid>

        <Grid item xs={4}>
          <PropertyInfo
            title="Kết quả:"
            value={
              (numberFormatAsString(
                firstItem?.quantificationResult?.toString() || "",
              ) || "-") + " g/m2"
            }
          ></PropertyInfo>
        </Grid>

        <Grid item xs={4}>
          <PropertyInfo
            title="Kết luận:"
            value={
              (firstItem?.quantificationConclusion &&
                getLabelOfConclusionStatus(
                  firstItem?.quantificationConclusion,
                )) ||
              ""
            }
          ></PropertyInfo>
        </Grid>
        <Grid item xs={12}>
          <Divider></Divider>
        </Grid>

        <Grid item xs={4}>
          <PropertyInfo
            title="Tiêu chuẩn - Độ cao hạt nổi:"
            value={`${firstItem?.product?.process?.foil || "-"} (mm)`}
          ></PropertyInfo>
        </Grid>

        <Grid item xs={4}>
          <PropertyInfo
            title="Kết quả:"
            value={
              (numberFormatAsString(firstItem?.foilResult?.toString() || "") ||
                "-") + " mm"
            }
          ></PropertyInfo>
        </Grid>

        <Grid item xs={4}>
          <PropertyInfo
            title="Kết luận:"
            value={
              (firstItem?.foilConclusion &&
                getLabelOfConclusionStatus(firstItem?.foilConclusion)) ||
              ""
            }
          ></PropertyInfo>
        </Grid>

        <Grid item xs={4}>
          <PropertyInfo
            title=" Kích thước toa gấp:"
            value={`${firstItem?.product?.dimensionWeight?.foldingStandardSize || ""} `}
          ></PropertyInfo>
        </Grid>

        <Grid item xs={4}>
          <PropertyInfo
            title="Kết quả:"
            value={firstItem?.foldingStandardSizeResult || ""}
          ></PropertyInfo>
        </Grid>

        <Grid item xs={4}>
          <PropertyInfo
            title="Kết luận:"
            value={
              (firstItem?.foldingStandardSizeConclusion &&
                getLabelOfConclusionStatus(
                  firstItem?.foldingStandardSizeConclusion,
                )) ||
              ""
            }
          ></PropertyInfo>
        </Grid>

        <Grid item xs={4}>
          <PropertyInfo
            title=" Số lần gấp:"
            value={`${firstItem?.product?.dimensionWeight?.foldingStandardCount || ""} `}
          ></PropertyInfo>
        </Grid>

        <Grid item xs={4}>
          <PropertyInfo
            title="Kết quả:"
            value={firstItem?.foldingStandardCountResult || ""}
          ></PropertyInfo>
        </Grid>

        <Grid item xs={4}>
          <PropertyInfo
            title="Kết luận:"
            value={
              (firstItem?.foldingStandardCountConclusion &&
                getLabelOfConclusionStatus(
                  firstItem?.foldingStandardCountConclusion,
                )) ||
              ""
            }
          ></PropertyInfo>
        </Grid>

        <Grid item xs={12}>
          <Divider></Divider>
        </Grid>

        <Grid item xs={12}>
          <PropertyInfo
            title="Số Hộp (toa)/ Bản In:"
            value={
              (numberFormatAsString(
                firstItem?.product?.process?.printPaperSP?.toString() || "",
              ) || "-") +
              " " +
              (firstItem?.printPaperSPUnit
                ? getLabelOfPrintPaperSPUnit(
                    firstItem?.printPaperSPUnit as PrintPaperSPUnitEnum,
                  )
                : "")
            }
          ></PropertyInfo>
        </Grid>
        <Grid item xs={12}>
          <Divider></Divider>
        </Grid>
        <Grid item xs={12}>
          <PropertyInfo
            title="Số lượng:"
            value={firstItem?.amount || ""}
          ></PropertyInfo>
        </Grid>
      </Grid>
    </Stack>
  );
});
